/**
 * Renders the "Scheda riepilogo ricevimento merce" PDF (T-182) client-side with
 * pdf-lib. A4 portrait. Mirrors the firma/footer of the `export-abbattimento-month`
 * edge function so the two HACCP compliance documents are consistent (same
 * normative wording: Reg. UE 910/2014 eIDAS, Reg. CE 852/2004 art. 5, ISO
 * 22000:2018 par. 7.5).
 *
 * WinAnsi-safe: StandardFonts cannot encode UTF-8 > CP1252, so every string goes
 * through `safe()` and we never emit emoji ("CONFORME"/"NON CONFORME" are text).
 * The original scan is appended at the end (image → embed, PDF → copyPages).
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont } from 'pdf-lib';
import {
  formatDateIt,
  formatDateTimeIt,
  UNKNOWN_OPERATOR,
  type CaricoInfo,
  type ConformityInfo,
} from './receiptModel.js';

/**
 * T-184/T-193: etichetta "ben espressa" dell'operatore per il blocco firma.
 * - nome risolto  → "Mario Rossi (Ristoratore)"  (ruolo solo come qualificatore)
 * - nome ignoto   → "Responsabile HACCP"  — col resolver tenant (T-193) questo caso
 *   è raro (operatore non più membro del tenant). In quel caso, per difendibilità,
 *   la firma ricade sul responsabile dell'autocontrollo HACCP (figura legalmente
 *   responsabile dell'establishment), mai su un ruolo operativo generico.
 * Il nome viene reso in GRASSETTO dal renderer (vedi `signatory`).
 */
function operatorDisplay(operatore: string, ruolo: string): string {
  const role = (ruolo ?? '').trim();
  const cap = role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
  const nameUnknown = !operatore || operatore.trim() === '' || operatore === UNKNOWN_OPERATOR;
  if (nameUnknown) return 'Responsabile HACCP';
  return cap ? `${operatore} (${cap})` : operatore;
}

/** "05/06/2026 08:36:04" in ora locale italiana (footer audit). '' se invalida. */
function formatDateTimeSecondsIt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export interface ReceiptLine {
  description: string;
  qty: number;
  uom: string;
  unitPrice: number | null;
  lineTotal: number | null;
  batchLot: string | null;
  expirationIso: string | null;
}

export interface ReceiptScan {
  bytes: Uint8Array;
  format: 'jpg' | 'png' | 'pdf';
}

export interface ReceiptModel {
  generatedAtIso: string;
  tenantName: string;
  businessName: string | null;
  tenantPiva: string | null;
  responsabileHaccp: string | null;
  manualRevision: { number: string; signedAtIso: string } | null;
  docTypeLabel: string; // 'DDT' | 'FATTURA' ...
  docNumber: string;
  docDateIso: string;
  docStatus: string;
  totalAmount: number | null;
  currency: string;
  supplier: { name: string; piva: string | null; address: string | null; category: string | null } | null;
  lines: ReceiptLine[];
  carico: CaricoInfo;
  conformity: ConformityInfo;
  documentSeal: string; // sha256 over carico+conformity events
  eventCount: number; // total events sealed (carico + conformità)
  eventMinRev: number;
  eventMaxRev: number;
  anchor: { provider: string; anchoredAtIso: string } | null;
}

// A4 portrait (pt)
const PAGE_W = 595;
const PAGE_H = 842;
const MARGIN_X = 40;
const MARGIN_Y = 40;
const CONTENT_W = PAGE_W - 2 * MARGIN_X;

const BLACK = rgb(0, 0, 0);
const GRAY = rgb(0.45, 0.45, 0.45);
const GRAY_LIGHT = rgb(0.82, 0.82, 0.82);
const RED = rgb(0.725, 0.107, 0.157);
const GREEN = rgb(0.086, 0.639, 0.286);
const ORANGE = rgb(0.917, 0.451, 0.0);
const HEAD_FILL = rgb(0.93, 0.95, 0.98);
const SECTION_FILL = rgb(0.95, 0.95, 0.95);

/**
 * WinAnsi-safe sanitize: pdf-lib StandardFonts encode CP1252 (Latin-1), so we
 * normalize to NFC to PRESERVE Italian accents (à è ì ò ù, all in CP1252),
 * fold the few common typographic glyphs that are NOT in CP1252, and replace
 * anything still outside the range with '?'. (Improves on the abbattimento
 * helper, which NFD-stripped accents.)
 */
function safe(s: string): string {
  if (!s) return '';
  return s
    .normalize('NFC')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/[→⇒]/g, '->')
    .replace(/[←⇐]/g, '<-')
    .replace(/[•]/g, '-')
    .replace(/✓/g, 'v')
    .replace(/✗/g, 'x')
    .replace(/[^\x20-\xff]/g, '?');
}

function truncate(s: string | null | undefined, maxChars: number): string {
  if (!s) return '';
  if (s.length <= maxChars) return s;
  return s.slice(0, maxChars - 1) + '...';
}

function euro(amount: number, currency: string): string {
  const sym = currency === 'EUR' || !currency ? 'EUR ' : `${currency} `;
  return `${sym}${amount.toFixed(2)}`;
}

export async function buildReceiptPdf(
  model: ReceiptModel,
  scan: ReceiptScan | null,
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const italic = await pdf.embedFont(StandardFonts.HelveticaOblique);
  pdf.setTitle(`Scheda riepilogo ricevimento - ${model.docTypeLabel} ${model.docNumber}`);
  pdf.setCreator('TrackEAT');
  pdf.setProducer('TrackEAT');

  let page = pdf.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN_Y;

  function newPage(): void {
    page = pdf.addPage([PAGE_W, PAGE_H]);
    y = PAGE_H - MARGIN_Y;
  }
  function ensureSpace(needed: number): void {
    if (y - needed < MARGIN_Y) newPage();
  }

  function drawTextAt(text: string, x: number, yy: number, font_: PDFFont, size: number, color = BLACK): void {
    page.drawText(safe(text), { x, y: yy, size, font: font_, color });
  }
  function centered(text: string, yy: number, font_: PDFFont, size: number, color = BLACK): void {
    const t = safe(text);
    const w = font_.widthOfTextAtSize(t, size);
    page.drawText(t, { x: MARGIN_X + Math.max(0, (CONTENT_W - w) / 2), y: yy, size, font: font_, color });
  }

  /** Word-wrap to content width; advances y; paginates. */
  function wrapped(
    text: string,
    font_: PDFFont,
    size: number,
    color = BLACK,
    opts: { indent?: number; lineGap?: number; maxWidth?: number } = {},
  ): void {
    const indent = opts.indent ?? 0;
    const lineGap = opts.lineGap ?? size * 0.45;
    const maxWidth = opts.maxWidth ?? CONTENT_W - indent;
    const words = safe(text).split(/\s+/).filter(Boolean);
    let line = '';
    const flush = (): void => {
      ensureSpace(size + lineGap);
      y -= size;
      page.drawText(safe(line), { x: MARGIN_X + indent, y, size, font: font_, color });
      y -= lineGap;
    };
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (font_.widthOfTextAtSize(candidate, size) > maxWidth && line) {
        flush();
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) flush();
  }

  function gap(n: number): void {
    y -= n;
  }

  /**
   * T-184: riga firma con NOME OPERATORE in GRASSETTO ed evidente, seguito dai
   * dettagli (data, righe, rev/esito) in piccolo grigio sotto. Sostituisce la
   * vecchia riga unica che produceva "(operatore) (ristoratore)".
   */
  function signatory(label: string, operatore: string, ruolo: string, tail: string): void {
    const who = operatorDisplay(operatore, ruolo);
    ensureSpace(14);
    y -= 9;
    drawTextAt(label, MARGIN_X, y, font, 8, BLACK);
    const lx = MARGIN_X + font.widthOfTextAtSize(safe(label), 8) + 4;
    drawTextAt(who, lx, y, bold, 9.5, BLACK);
    y -= 3;
    if (tail) wrapped(tail, font, 7.5, GRAY, { indent: 10 });
  }

  function sectionHeading(label: string): void {
    ensureSpace(22);
    y -= 16;
    page.drawRectangle({ x: MARGIN_X, y: y - 4, width: CONTENT_W, height: 18, color: SECTION_FILL });
    drawTextAt(label, MARGIN_X + 4, y + 1, bold, 10, BLACK);
    y -= 12;
  }

  function rule(): void {
    ensureSpace(8);
    page.drawLine({
      start: { x: MARGIN_X, y },
      end: { x: PAGE_W - MARGIN_X, y },
      thickness: 0.5,
      color: GRAY_LIGHT,
    });
    y -= 6;
  }

  // ── Intestazione ──────────────────────────────────────────────────────────
  y -= 6;
  centered('SCHEDA RIEPILOGO RICEVIMENTO MERCE', y, bold, 16, BLACK);
  y -= 20;
  const sub = `${model.tenantName}${model.businessName ? ' - ' + model.businessName : ''}`;
  centered(sub, y, font, 10, GRAY);
  y -= 13;
  if (model.tenantPiva) {
    centered(`P.IVA ${model.tenantPiva}`, y, font, 8, GRAY);
    y -= 11;
  }
  centered(`Generato il ${formatDateTimeIt(model.generatedAtIso)}`, y, font, 8, GRAY);
  y -= 6;
  rule();

  // ── Documento ─────────────────────────────────────────────────────────────
  sectionHeading('DOCUMENTO');
  wrapped(
    `${model.docTypeLabel} N. ${model.docNumber}   -   ${formatDateIt(model.docDateIso)}   -   Stato: ${model.docStatus}`,
    font,
    9.5,
  );
  if (model.totalAmount != null) {
    wrapped(`Totale documento: ${euro(model.totalAmount, model.currency)}`, font, 9.5);
  } else {
    wrapped('Importo non riportato (tipico dei DDT).', italic, 9, GRAY);
  }

  // ── Fornitore ─────────────────────────────────────────────────────────────
  if (model.supplier) {
    sectionHeading('FORNITORE');
    wrapped(model.supplier.name, bold, 10);
    if (model.supplier.piva) wrapped(`P.IVA ${model.supplier.piva}`, font, 9, GRAY);
    if (model.supplier.address) wrapped(model.supplier.address, font, 9, GRAY);
    if (model.supplier.category) wrapped(`Categoria: ${model.supplier.category}`, font, 9, GRAY);
  }

  // ── Righe ─────────────────────────────────────────────────────────────────
  sectionHeading(`RIGHE DOCUMENTO (${model.lines.length})`);
  const cols = [
    { key: 'descrizione', label: 'Descrizione', w: 245, align: 'left' as const },
    { key: 'qty', label: 'Q.ta', w: 70, align: 'center' as const },
    { key: 'lotto', label: 'Lotto', w: 110, align: 'left' as const },
    { key: 'scadenza', label: 'Scadenza', w: 90, align: 'center' as const },
  ];
  const rowH = 18;
  const headH = 18;

  const drawCellText = (
    text: string,
    x: number,
    w: number,
    cellY: number,
    align: 'left' | 'center',
    font_: PDFFont,
    size: number,
    color = BLACK,
  ): void => {
    const t = safe(truncate(text, Math.floor(w / (size * 0.5))));
    const tw = font_.widthOfTextAtSize(t, size);
    const tx = align === 'center' ? x + (w - tw) / 2 : x + 3;
    page.drawText(t, { x: Math.max(x + 2, tx), y: cellY, size, font: font_, color });
  };

  const drawRowBorders = (cellY: number, h: number): void => {
    let x = MARGIN_X;
    for (const c of cols) {
      page.drawRectangle({ x, y: cellY, width: c.w, height: h, borderColor: GRAY_LIGHT, borderWidth: 0.5 });
      x += c.w;
    }
  };

  const drawTableHeader = (): void => {
    ensureSpace(headH + 2);
    y -= headH;
    page.drawRectangle({ x: MARGIN_X, y, width: CONTENT_W, height: headH, color: HEAD_FILL });
    let x = MARGIN_X;
    for (const c of cols) {
      page.drawRectangle({ x, y, width: c.w, height: headH, borderColor: GRAY, borderWidth: 0.6 });
      drawCellText(c.label, x, c.w, y + 5, c.align === 'center' ? 'center' : 'left', bold, 8);
      x += c.w;
    }
  };

  drawTableHeader();
  if (model.lines.length === 0) {
    ensureSpace(rowH);
    y -= rowH;
    page.drawRectangle({ x: MARGIN_X, y, width: CONTENT_W, height: rowH, borderColor: GRAY_LIGHT, borderWidth: 0.5 });
    drawCellText('Nessuna riga registrata per questo documento.', MARGIN_X, CONTENT_W, y + 5, 'left', italic, 8, GRAY);
  } else {
    for (const l of model.lines) {
      ensureSpace(rowH);
      // Repeat the header when a page break happens mid-table.
      if (y - rowH < MARGIN_Y) {
        newPage();
        drawTableHeader();
      }
      y -= rowH;
      drawRowBorders(y, rowH);
      let x = MARGIN_X;
      const qtyText = `${l.qty}${l.uom ? ' ' + l.uom : ''}`;
      const values: Record<string, string> = {
        descrizione: l.description || '-',
        qty: qtyText,
        lotto: l.batchLot || '-',
        scadenza: l.expirationIso ? formatDateIt(l.expirationIso) : '-',
      };
      for (const c of cols) {
        drawCellText(values[c.key] ?? '', x, c.w, y + 5, c.align, font, 8);
        x += c.w;
      }
    }
  }
  gap(2);

  // ── Controllo conformità ────────────────────────────────────────────────────
  sectionHeading('CONTROLLO CONFORMITA HACCP AL RICEVIMENTO');
  const conf: ConformityInfo = model.conformity;
  if (conf.present) {
    const esitoColor = conf.conform ? GREEN : ORANGE;
    ensureSpace(14);
    y -= 12;
    drawTextAt('Esito: ', MARGIN_X, y, bold, 10, BLACK);
    drawTextAt(conf.esitoLabel, MARGIN_X + bold.widthOfTextAtSize('Esito: ', 10), y, bold, 10, esitoColor);
    y -= 4;
    signatory('Verificato da', conf.operatore, conf.ruolo, `il ${formatDateTimeIt(conf.checkedAtIso)}.`);
    if (conf.disclaimerItems.length) {
      wrapped('Voci controllate al ricevimento:', font, 8.5, GRAY);
      for (const item of conf.disclaimerItems) {
        wrapped(`-  ${item}`, font, 8.5, BLACK, { indent: 8 });
      }
    }
    if (!conf.conform && conf.notes) {
      gap(2);
      wrapped(`Non conformita' riscontrate: ${conf.notes}`, font, 9, ORANGE);
    }
  } else {
    wrapped('Controllo conformita\' non registrato per questo documento.', italic, 9, GRAY);
  }

  // ── Firma elettronica e certificazione ──────────────────────────────────────
  gap(4);
  ensureSpace(20);
  y -= 14;
  page.drawRectangle({ x: MARGIN_X, y: y - 4, width: CONTENT_W, height: 18, color: rgb(0.98, 0.93, 0.93) });
  drawTextAt('FIRMA ELETTRONICA E CERTIFICAZIONE DIGITALE', MARGIN_X + 4, y + 1, bold, 10, RED);
  y -= 14;

  wrapped(
    "Firma elettronica (Reg. UE 910/2014 - eIDAS): ogni operazione e' attribuita all'operatore autenticato che l'ha eseguita; identita' verificata all'accesso.",
    font,
    8,
  );
  gap(3);

  // Carico merce
  if (model.carico.present) {
    signatory(
      'Carico merce registrato da:',
      model.carico.operatore,
      model.carico.ruolo,
      `il ${formatDateTimeIt(model.carico.dataIso)} - ${model.carico.righe} righe. Rev. catena #${model.carico.revMin}-#${model.carico.revMax}.`,
    );
    if (model.carico.seal) {
      wrapped(`Sigillo carico (SHA-256): ${model.carico.seal}`, font, 7.5, GRAY, { indent: 8 });
    }
  } else {
    wrapped('Carico merce: nessun evento di carico associato a questo documento.', italic, 8, GRAY);
  }
  gap(2);

  // Conformità firma
  if (conf.present) {
    signatory(
      "Conformita' confermata da:",
      conf.operatore,
      conf.ruolo,
      `il ${formatDateTimeIt(conf.checkedAtIso)} - esito ${conf.esitoLabel}.`,
    );
    if (conf.selfHash) {
      wrapped(`Hash evento conformita' (SHA-256): ${conf.selfHash}`, font, 7.5, GRAY, { indent: 8 });
    }
  } else {
    wrapped("Conformita': controllo non registrato per questo documento.", italic, 8, GRAY);
  }
  gap(3);

  // Responsabile + manuale
  const respName = model.responsabileHaccp;
  const respLine = respName
    ? `Responsabile autocontrollo HACCP: ${respName}.`
    : 'Responsabile autocontrollo HACCP: (da impostare nel profilo del locale).';
  const manualLine = model.manualRevision
    ? ` Manuale HACCP rev. ${model.manualRevision.number} firmato il ${formatDateIt(model.manualRevision.signedAtIso)}.`
    : ' Manuale HACCP: nessuna revisione firmata.';
  wrapped(respLine + manualLine, font, 8);
  gap(2);

  // Integrità documento
  wrapped(
    `Integrita': hash SHA-256 concatenati (catena immutabile). Hash documento: ${model.documentSeal || '(nessun evento)'}`,
    font,
    8,
  );
  gap(2);

  // Eventi + marca temporale (mirror della Scheda Abbattimento)
  const eventiPrefix = `Eventi: ${model.eventCount} (rev. ${model.eventMinRev}-${model.eventMaxRev}).`;
  const stampLine = model.anchor
    ? `${eventiPrefix} Marca temporale RFC3161: ${model.anchor.provider} @ ${formatDateTimeIt(model.anchor.anchoredAtIso)}.`
    : `${eventiPrefix} Marca temporale RFC3161: in attesa (cron settimanale).`;
  wrapped(stampLine, font, 8, model.anchor ? GREEN : ORANGE);
  gap(2);

  wrapped(
    "Conforme a Reg. CE 852/2004 art. 5 (autocontrollo) e ISO 22000:2018 par. 7.5 (informazioni documentate). Verifica integrita': trackeat.eu.",
    font,
    8,
    GRAY,
  );
  gap(4);
  rule();
  wrapped(
    'Documento riassuntivo generato da TrackEAT. Non sostituisce la scheda di ricevimento merce ufficiale.',
    italic,
    7.5,
    GRAY,
  );
  wrapped(
    `Generato il ${formatDateTimeSecondsIt(model.generatedAtIso)} (ora italiana) - TrackEAT - trackeat.eu`,
    font,
    7.5,
    GRAY,
  );

  // ── Scansione originale in coda ─────────────────────────────────────────────
  if (scan) {
    await appendScan(pdf, scan, font, bold);
  }

  return pdf.save();
}

/** Append the original DDT/fattura scan: image embedded full-page, PDF merged. */
async function appendScan(
  pdf: PDFDocument,
  scan: ReceiptScan,
  font: PDFFont,
  bold: PDFFont,
): Promise<void> {
  if (scan.format === 'pdf') {
    try {
      const src = await PDFDocument.load(scan.bytes, { ignoreEncryption: true });
      const indices = src.getPageIndices();
      const copied = await pdf.copyPages(src, indices);
      for (const p of copied) pdf.addPage(p);
      return;
    } catch {
      const page = pdf.addPage([PAGE_W, PAGE_H]);
      page.drawText(safe('SCANSIONE ORIGINALE'), { x: MARGIN_X, y: PAGE_H - MARGIN_Y - 14, size: 12, font: bold, color: BLACK });
      page.drawText(
        safe("Il PDF originale non e' incorporabile. E' disponibile tramite 'Apri file originale' nell'app."),
        { x: MARGIN_X, y: PAGE_H - MARGIN_Y - 36, size: 9, font, color: GRAY },
      );
      return;
    }
  }

  // image
  const page = pdf.addPage([PAGE_W, PAGE_H]);
  page.drawText(safe('SCANSIONE ORIGINALE DEL DOCUMENTO'), {
    x: MARGIN_X,
    y: PAGE_H - MARGIN_Y - 12,
    size: 11,
    font: bold,
    color: BLACK,
  });
  try {
    const img = scan.format === 'png' ? await pdf.embedPng(scan.bytes) : await pdf.embedJpg(scan.bytes);
    const topY = PAGE_H - MARGIN_Y - 24;
    const availW = CONTENT_W;
    const availH = topY - MARGIN_Y;
    const scaleFactor = Math.min(availW / img.width, availH / img.height, 1);
    const w = img.width * scaleFactor;
    const h = img.height * scaleFactor;
    page.drawImage(img, {
      x: MARGIN_X + (availW - w) / 2,
      y: topY - h,
      width: w,
      height: h,
    });
  } catch {
    page.drawText(
      safe("Anteprima della scansione non disponibile. Usa 'Apri file originale' nell'app."),
      { x: MARGIN_X, y: PAGE_H - MARGIN_Y - 48, size: 9, font, color: GRAY },
    );
  }
}
